/*
/*
* Report page
*/
import * as _ from 'lodash';
import * as React from 'react';
import * as WordWrap from 'word-wrap';

import { AppDataConstants } from '../../../common/app-data-constants';
import { Formatters } from '../../../common/formatters';
import * as ReportComponents from '../report-components';

export const Render = ({ registerFunc, refFunc, scrollToFunc, maxTableRowsPerPage = 0, pageTitle = '', className = '', pageConfiguration, pageCount, reportData, appText, ...rest }) => {
    const sectionName = ReportComponents.ReportSectionNames.significantComponents;
    const ReportPageWithRegister = ReportComponents.withPageRegistration(ReportComponents.ReportPageTableData, sectionName, registerFunc, refFunc, scrollToFunc);
    const getTableRowKey = (id, secName, tabName = 'data') => ReportComponents.getRowKey(id, secName, tabName);

    // Configuration
    const maxComponentNameLenWrap = 75;
    const chunkSize = (maxTableRowsPerPage > 0) ? maxTableRowsPerPage : _.get(pageConfiguration, 'maxTableRowsPerPage', 15);
    const { formData, association, inventory, currentOrder, currentOrderChart, percentFunded, yearlySummaryData } = reportData;
    const reportOptionsGroupComponents = Formatters.getBool(currentOrderChart, 'groupComponents');

    // Helpers
    const extendDataItem = (dataItem: any, sumAvgCostsPerUL: number = 0) => {
        let avgCost = _.get(dataItem, 'avgCost_sort', 0);
        const usefulLifeVal = _.get(dataItem, 'usefulLife', '');
        let usefulLife = _.get(dataItem, 'usefulLife_sort', 0);
        const invExpenditure = _.get(dataItem, 'expenditureMap[0]');
        let idealBal = _.get(invExpenditure, 'hypoProjectedExpenseShare', 0);
        let curBal = _.get(invExpenditure, 'projectedExpenseShare', 0);
        let avgCostPerUL = _.get(invExpenditure, 'signAsDollar', 0);
        const subgroup = _.get(dataItem, 'subgroup', '');

        let percent = 0;
        if (avgCostPerUL > 0 && sumAvgCostsPerUL > 0) {
            percent = (avgCostPerUL / sumAvgCostsPerUL);
        }

        let usefulLifeText = usefulLife;
        if (usefulLife === 0) {
            let newUsefulLife = usefulLifeVal;
            if (usefulLifeVal !== AppDataConstants.UsefulLifeOptions.OneTimeExpense) {
                newUsefulLife = AppDataConstants.UsefulLifeOptions.NotApplicable;
            }

            usefulLifeText = newUsefulLife;
        }

        if (dataItem.usefulLife === 'N/A') {
            idealBal = 0;
            curBal = 0;
            avgCostPerUL = 0;
            percent = 0;
        }

        const item = {
            avgCost: avgCost,
            avgCostPerUL: avgCostPerUL,
            curBal: curBal,
            idealBal: idealBal,
            percent: percent,
            usefulLife: usefulLifeText,
            subgroup: subgroup,
            wrappedItemRow: false,
            wrappedItemRow_sort: 0,
        };

        return item;
    };

    const transformDataItems = (dataItems: any[]) => {
        const sumAvgCostsPerUL = _.sumBy(dataItems, 'expenditureMap[0].signAsDollar');

        let results: any[] = _(dataItems)
            .map((inv, i) => {
                const extended = extendDataItem(inv, sumAvgCostsPerUL);
                const itemCompName = _.trim(Formatters.getStr(inv, 'componentName'));

                const item = {
                    _id: inv._id,
                    avgCost: 0,
                    avgCostPerUL: 0,
                    componentId: (_.get(inv, 'componentDecimal')) ? (_.toString(Formatters.getInt(inv, 'componentId') + '.' + Formatters.getStr(inv, 'componentDecimal'))) : Formatters.getInt(inv, 'componentId'),
                    componentName: itemCompName,
                    componentName_sort: itemCompName.toLowerCase(),
                    curBal: 0,
                    idealBal: 0,
                    percent: 0,
                    quantity: _.get(inv, 'quantity', ''),
                    remainingUsefulLife: _.get(inv, 'remainingUsefulLife', 0),
                    usefulLife: 0,
                };

                const res = _.merge({}, item, extended);

                return res;
            })
            .value();

        let resultsExpanded = _.stubArray();
        _.forEach(results, (r) => {
            let rItem = _.cloneDeep(r);

            const rItemCompName = Formatters.getStr(rItem, 'componentName');
            const rItemCompNameWrapResult = WordWrap(rItemCompName, {
                width: maxComponentNameLenWrap,
                trim: true,
                indent: '',
                cut: false,
            });
            const rItemCompNameWrapSplitResults: any = _.split(rItemCompNameWrapResult, '\n');
            const needCompNameWrapsNum = (_.size(rItemCompNameWrapSplitResults) <= 1) ? 0 : _.size(rItemCompNameWrapSplitResults) - 1;
            const needCompNameWrap = (needCompNameWrapsNum >= 1) ? true : false;

            if (needCompNameWrap) {
                let maxNeedCompWraps: any = needCompNameWrapsNum;

                for (let iWrapItemInd = 0; iWrapItemInd <= maxNeedCompWraps; iWrapItemInd++) {
                    let rWrapInvItem = _.cloneDeep(rItem);

                    const rWrapInvItemCompName = Formatters.getStr(rItemCompNameWrapSplitResults, `[${iWrapItemInd}]`);
                    _.set(rWrapInvItem, 'componentName', rWrapInvItemCompName);

                    if (iWrapItemInd) {
                        _.set(rWrapInvItem, 'remainingUsefulLife', '');
                        _.set(rWrapInvItem, 'usefulLife', '');
                        _.set(rWrapInvItem, 'avgCost', 0);
                        _.set(rWrapInvItem, 'avgCostPerUL', 0);
                        _.set(rWrapInvItem, 'wrappedItemRow', true);
                        _.set(rWrapInvItem, 'wrappedItemRow_sort', iWrapItemInd);
                    }

                    resultsExpanded.push(rWrapInvItem);
                }
            } else {
                resultsExpanded.push(rItem);
            }
        });

        return resultsExpanded;
    };

    const buildRowsNew = (data: any[], tableName: string = 'tbl') => {
        const dataItems = _.filter(data, (o) => !Formatters.getBool(o, 'placeholder'));
        let rows: any = _(dataItems)
            .map((item: any, ind: number, arr: any[]) => {
                const isFirstPage = Formatters.getBool(item, 'group.first');
                const isLastPage = Formatters.getBool(item, 'group.last');
                const isDisplayItemOnly = Formatters.getBool(item, 'wrappedItemRow');

                let res = _.stubArray();

                if (isFirstPage && reportOptionsGroupComponents) {
                    const headerRow = (isFirstPage) ? buildGroupHeaderRow(item) : undefined;
                    const hasHeaderRow = (headerRow) ? true : false;

                    if (hasHeaderRow) { res.push(headerRow); }
                }

                res.push(
                    <tr key={getTableRowKey(`${Formatters.getStr(item, '_id')}-${Formatters.getStr(item, 'wrappedItemRow_sort')}`, sectionName, `${tableName}-sigcomp-row-component`)}>
                        <td className="text-center">{(!isDisplayItemOnly) ? item.componentId : <span />}</td>
                        <td>{item.componentName}</td>
                        <td className="text-center">{(!isDisplayItemOnly) ? item.usefulLife : <span />}</td>
                        <td className="text-center">{(!isDisplayItemOnly) ? item.remainingUsefulLife : <span />}</td>
                        <td className="text-right">{(!isDisplayItemOnly) ? <ReportComponents.Currency value={item.avgCost} decimals={0} /> : <span />}</td>
                        <td className="text-right">{(!isDisplayItemOnly) ? <ReportComponents.Currency value={item.avgCostPerUL} decimals={0} /> : <span />}</td>
                        <td className="text-right">{(!isDisplayItemOnly) ? <ReportComponents.Percentage value={item.percent} decimals={2} /> : <span />}</td>
                    </tr>
                );

                return res;
            })
            .value();

        return _.flattenDeep(rows);
    };

    const transformedInvItems = transformDataItems(inventory);

    const invSort = _.orderBy(transformedInvItems, ['componentId', 'componentName_sort', 'wrappedItemRow_sort'], ['asc', 'asc', 'asc', 'asc']);

    // GROUP ITEMS BY SUBGROUP
    const grouped = _(invSort)
        .groupBy('subgroup')
        .toPairs()
        .value();

    const subgroups = _.flattenDeep(
        grouped.map((g, gInd) => {
            // const sg = _.find(association.inventorySubgroups, ['groupCode', g[0]]);
            const sg = (_.find(association.inventorySubgroups, ['groupCode', g[0]])) ? _.find(association.inventorySubgroups, ['groupCode', g[0]]) : _.find(association.inventorySubgroups, ['groupCode', _.toString(_.parseInt(g[0]))]);
            if (!sg || !sg.groupCode) { return null; }

            const items = g[1].map((i, iInd, iArr) => {
                return _.merge({}, i, {
                    group: {
                        first: (iInd === 0) ? true : false,
                        last: (iInd === _.size(iArr) - 1) ? true : false,
                        groupInd: iInd,
                        subgroupCode: sg.groupCode,
                        subgroupTitle: sg.title,
                    }
                });
            });

            return items;
        })
    );

    const buildGroupHeaderRow = (itemData: any) => {
        const groupTitle = _.get(itemData, 'group.subgroupTitle', '');
        const groupSubgroupCode = Formatters.getStr(itemData, 'subgroup');

        const result = (
            <tr key={getTableRowKey(`sigcomp-summary-row-header-subgroup-${groupSubgroupCode}`, sectionName, 'tbl-sigcomp-group-header')} className="pdf-data-table-summary-row">
                <td colSpan={7} className="pdf-data-table-summary-row-colspan text-left">{groupTitle}</td>
            </tr>
        );

        return result;
    };

    // CHUNK DATA/TABLES TO PAGES
    const subgroupsSorted = _.orderBy(subgroups, ['subgroup', 'group.subgroupTitle', 'group.groupInd']);

    let tableRows = (reportOptionsGroupComponents) ? buildRowsNew(subgroupsSorted, 'buildRowsSigCompTable-new') : buildRowsNew(invSort, 'buildRowsSigCompTable-new');

    const pagesNew = _(tableRows)
        .chunk(chunkSize)
        .map((chunkItems, chunkInd, chunkInputArr) => {
            const keyPrefix = `chunkInd-sigcomp-${chunkInd}`;

            const headRow = (
                <tr>

                    <th className="col-sigcomp-id">{appText('page.significant-components.grid-column-headers.id')}</th>
                    <th className="col-sm-4 col-sigcomp-component-name">{appText('page.significant-components.grid-column-headers.component-name')}</th>
                    <th className="col-sigcomp-ul">{appText('page.significant-components.grid-column-headers.ul')}</th>
                    <th className="col-sigcomp-rul">{appText('page.significant-components.grid-column-headers.rul')}</th>
                    <th className="col-sigcomp-average-current">{appText('page.significant-components.grid-column-headers.average-current')}</th>
                    <th colSpan={2} className="col-sigcomp-significance">
                        <table className="pdf-data-table-significant-components-cost-ul">
                            <thead>
                                <tr>
                                    <th colSpan={2} className="text-center">{appText('page.significant-components.grid-column-headers.significance-curr-cost-ul')}</th>
                                </tr>
                                <tr>
                                    <th className="pdf-data-table-significant-components-cost-ul-left text-right">{appText('page.significant-components.grid-column-headers.significance-as-currency')}</th>
                                    <th className="pdf-data-table-significant-components-cost-ul-right text-right">{appText('page.significant-components.grid-column-headers.significance-as-percentage')}</th>
                                </tr>
                            </thead>
                        </table>
                    </th>
                </tr>
            );

            const chunkPage = (
                <div key={getTableRowKey(`sigcomp-page-keyPrefix-${keyPrefix}`, sectionName, 'tbl-sigcomp-chunk-page')} className="pdf-data-table-significant-components-chunkpage">
                    <ReportPageWithRegister
                        className={className}
                        classNameTable="pdf-data-table-significant-components"
                        footerData={reportData}
                        pageIndex={chunkInd}
                        pageTitle={pageTitle}
                        showFooter={true}
                        tbodyRows={chunkItems}
                        theadRow={headRow}
                        totalPageCount={pageCount}
                    />
                </div>
            );

            return chunkPage;
        })
        .value();

    return (
        <div className="pdf-data-table-significant-component-pages-wrapper">
            {pagesNew}
        </div>
    );
};



// WEBPACK FOOTER //
// ./src/report/pdf/pages/report-page-significant-components.tsx